import type { EmployeeProfilesDescriptionDto } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/types";
import type { ExcelDownloadClient, ExcelDownloadResult } from "app/components/React/ExcelButton/ExcelClientDownload";
import type { GetRequestOptions, IFetchService } from "app/services/fetchService";
import type CompanyStructureDto from "../../../company/models/company-structure/CompanyStructureDto";
import type CompanyStructureRequestParams from "../../../company/models/company-structure/CompanyStructureRequestParams";
import type {
    AnnualPageviewsInfoDto,
    AnnualPageviewsInfoRequestParams,
} from "../../../company/types/annual-page-views";
import type { ECommerceOverviewDto, ECommerceOverviewRequestParams } from "../../../company/types/e-commerce-overview";
import type { VisitsOverTimeDto, VisitsOverTimeRequestParams } from "../../../company/types/monthly-visits-overtime";
import type { SignalEventsDataDto, SignalEventsRequestParams } from "../../../company/types/signals";
import type { TotalVisitsInfoDto, TotalVisitsInfoRequestParams } from "../../../company/types/total-visits";
import type { GeoBreakdownDto, GeoBreakdownRequestParams } from "../../../company/types/visits-geo-breakdown";
import type { CompanyInfo } from "../../types/company";
import type { CountryVisitsShare } from "../../types/countries";
import type { BaseSettingsDto } from "../../types/page-base-settings";
import type {
    AdNetworksPieChartDto,
    AdNetworksRequestParams,
    AdNetworksTableDto,
} from "../../../company/types/ad-networks";

export default function createAccountOverviewApiService(
    fetchService: IFetchService,
    excelDownloadClient: ExcelDownloadClient,
) {
    return {
        fetchBaseSettings(domain: string, options?: GetRequestOptions): Promise<BaseSettingsDto> {
            return fetchService.get(`/sales-api/account-overview/settings/${domain}`, undefined, options);
        },
        fetchTopLevelDomains(domain: string, options?: GetRequestOptions): Promise<string[]> {
            return fetchService.get("/sales-api/account-overview/country-domains", { domain }, options);
        },
        fetchCompanyInfo(domain: string, options?: GetRequestOptions): Promise<CompanyInfo> {
            return fetchService.get("/sales-api/account-overview/widget/base-info", { domain }, options);
        },
        fetchCountriesVisitsShares(
            domains: readonly string[],
            options?: GetRequestOptions,
        ): Promise<CountryVisitsShare[]> {
            return fetchService.get(
                "/sales-api/account-overview/country-shares",
                {
                    domains: domains.join(","),
                },
                options,
            );
        },
        fetchAnnualPageViewsInfo(
            params: AnnualPageviewsInfoRequestParams,
            options?: GetRequestOptions,
        ): Promise<AnnualPageviewsInfoDto> {
            const { countries, domains, ...rest } = params;

            return fetchService.get(
                "/sales-api/account-overview/widget/page-views/single-metric",
                {
                    ...rest,
                    countries: countries.join(","),
                    domains: domains.join(","),
                },
                options,
            );
        },
        fetchGeoBreakdownTableData(
            params: GeoBreakdownRequestParams,
            options?: GetRequestOptions,
        ): Promise<GeoBreakdownDto> {
            const { domains, ...rest } = params;

            return fetchService.get(
                "/sales-api/account-overview/widget/visits-geo-breakdown/table",
                {
                    ...rest,
                    domains: domains.join(","),
                },
                options,
            );
        },
        exportGeoBreakdownDataToExcel(
            params: Pick<GeoBreakdownRequestParams, "domains" | "webSource">,
        ): Promise<ExcelDownloadResult> {
            const urlParams = `webSource=${params.webSource}&domains=${params.domains.join(",")}`;

            return excelDownloadClient(`/sales-api/account-overview/widget/visits-geo-breakdown/excel?${urlParams}`);
        },
        fetchTotalVisitsInfo(
            params: TotalVisitsInfoRequestParams,
            options?: GetRequestOptions,
        ): Promise<TotalVisitsInfoDto> {
            return fetchService.get("sales-api/account-overview/widget/total-visits/single-metric", params, options);
        },
        fetchMonthlyVisitsInfo(
            params: VisitsOverTimeRequestParams,
            options?: GetRequestOptions,
        ): Promise<VisitsOverTimeDto> {
            return fetchService.get(
                "/sales-api/account-overview/widget/monthly-visits-overtime/GraphPOP",
                params,
                options,
            );
        },
        fetchDecisionMakersDescription(options?: GetRequestOptions): Promise<EmployeeProfilesDescriptionDto> {
            return fetchService.get("/sales-api/contacts/description", undefined, options);
        },
        fetchSignalEvents(
            params: SignalEventsRequestParams,
            options?: GetRequestOptions,
        ): Promise<SignalEventsDataDto> {
            return fetchService.get(
                "/sales-api/account-overview/signals",
                {
                    ...params,
                    take: 500,
                },
                options,
            );
        },
        fetchSignalEventsForTooltip(
            params: SignalEventsRequestParams,
            options?: GetRequestOptions,
        ): Promise<SignalEventsDataDto> {
            return fetchService.get(
                "/sales-api/account-overview/signals/tooltip",
                {
                    ...params,
                    take: 500,
                },
                options,
            );
        },
        fetchCompanyStructure(
            params: CompanyStructureRequestParams,
            options?: GetRequestOptions,
        ): Promise<CompanyStructureDto> {
            return fetchService.get("sales-api/account-overview/widget/org-tree", params, options);
        },
        fetchECommerceOverview(
            params: ECommerceOverviewRequestParams,
            options?: GetRequestOptions,
        ): Promise<ECommerceOverviewDto> {
            const { domains } = params;

            return fetchService.get(
                "/sales-api/account-overview/widget/ecommerce-info/single-metric",
                {
                    domains: domains.join(","),
                },
                options,
            );
        },
        reportECommerceOverview(domain: string): Promise<void> {
            return fetchService.post(`/sales-api/account-overview/widget/ecommerce-info/report?domain=${domain}`);
        },
        fetchAdNetworksPieChart(
            params: AdNetworksRequestParams,
            options?: GetRequestOptions,
        ): Promise<AdNetworksPieChartDto> {
            return fetchService.get("/sales-api/account-overview/ad-networks/pie-chart", params, options);
        },
        fetchAdNetworksTable(
            params: AdNetworksRequestParams,
            options?: GetRequestOptions,
        ): Promise<AdNetworksTableDto> {
            return fetchService.get("/sales-api/account-overview/ad-networks/table", params, options);
        },
    };
}
